import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Building2, Plus, Users, Crown, Trash2, UserPlus, Loader2,
  Wallet, Calendar, Truck, Megaphone, FileText, ClipboardList, 
  Handshake, Shield, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Predefined committee templates
const COMMITTEE_TEMPLATES = [
  {
    id: 'executive',
    name: 'Executive Committee',
    description: 'Core leadership responsible for overall direction, decision-making, budget approval, and coordination with school administration.',
    icon: Shield,
    color: 'bg-maroon',
    roles: ['Batch President/Chairperson', 'Vice President', 'Secretary', 'Treasurer'],
    appModules: ['Dashboard', 'Audit Log'],
    isRequired: true
  },
  {
    id: 'finance',
    name: 'Finance & Fundraising Committee',
    description: 'Manages reunion fees, payments, donations, GCash/bank accounts, and prepares financial reports.',
    icon: Wallet,
    color: 'bg-green-600',
    roles: ['Finance Head', 'Treasurer', 'Collection Officer'],
    appModules: ['Payments', 'Documents'],
    isRequired: true
  },
  {
    id: 'program',
    name: 'Program Committee',
    description: 'Designs flow of program, assigns hosts and speakers, prepares scripts and run sheets.',
    icon: Calendar,
    color: 'bg-purple-600',
    roles: ['Program Head', 'Script Writer', 'Stage Manager'],
    appModules: ['Program Flow', 'Documents'],
    isRequired: true
  },
  {
    id: 'logistics',
    name: 'Logistics & Venue Committee',
    description: 'Handles venue coordination, catering, sound & lights, seating arrangements, and registration desk setup.',
    icon: Truck,
    color: 'bg-blue-600',
    roles: ['Logistics Head', 'Venue Coordinator', 'Catering Coordinator'],
    appModules: ['Documents', 'Program'],
    isRequired: true
  },
  {
    id: 'communications',
    name: 'Communications & Outreach Committee',
    description: 'Locates batchmates, manages FB groups and emails, sends announcements and invitations.',
    icon: Megaphone,
    color: 'bg-orange-500',
    roles: ['Communications Head', 'Social Media Manager', 'Outreach Coordinator'],
    appModules: ['Announcements'],
    isRequired: true
  },
  {
    id: 'documentation',
    name: 'Documentation Committee',
    description: 'Handles meeting minutes, photo & video coverage, archives, and post-event reports.',
    icon: FileText,
    color: 'bg-teal-600',
    roles: ['Documentation Head', 'Photographer', 'Videographer', 'Archivist'],
    appModules: ['Document Vault'],
    isRequired: true
  },
  {
    id: 'registration',
    name: 'Registration & Attendance Committee',
    description: 'Manages on-site registration, attendance tracking, name tags/IDs, and distribution of kits.',
    icon: ClipboardList,
    color: 'bg-indigo-600',
    roles: ['Registration Head', 'Attendance Officer'],
    appModules: ['Attendees', 'Check-in'],
    isRequired: false
  },
  {
    id: 'sponsorship',
    name: 'Sponsorship & Partnerships Committee',
    description: 'Solicits sponsors, coordinates ads and acknowledgments, manages sponsor benefits.',
    icon: Handshake,
    color: 'bg-amber-600',
    roles: ['Sponsorship Head', 'Partner Relations'],
    appModules: ['Documents', 'Payments'],
    isRequired: false
  }
];

const Committees = () => {
  const { user, isOrganizer } = useAuth();
  const [searchParams] = useSearchParams();
  const [committees, setCommittees] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedCommittee, setSelectedCommittee] = useState(null);
  const [newCommittee, setNewCommittee] = useState({ name: '', description: '' });
  const [assignData, setAssignData] = useState({ user_id: '', role: 'member' });
  const [selectedTemplates, setSelectedTemplates] = useState(
    COMMITTEE_TEMPLATES.filter(t => t.isRequired).map(t => t.id)
  );
  const [creatingFromTemplate, setCreatingFromTemplate] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      fetchData();
    }
  }, [selectedEventId]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/events/my`);
      setEvents(response.data);
      if (response.data.length > 0) {
        // Use event from URL params or first event
        const urlEventId = searchParams.get('event_id');
        const eventId = urlEventId || response.data[0].id;
        setSelectedEventId(eventId);
      } else {
        setLoading(false);
      }
    } catch (error) {
      toast.error('Failed to load events');
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [committeesRes, attendeesRes] = await Promise.all([
        axios.get(`${API_URL}/api/event/committees?event_id=${selectedEventId}`),
        axios.get(`${API_URL}/api/event/attendees`)
      ]);
      setCommittees(committeesRes.data);
      setAttendees(attendeesRes.data);
    } catch (error) {
      console.error('Failed to load committees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCommittee = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/event/committees?event_id=${selectedEventId}`, newCommittee);
      toast.success('Committee created successfully');
      setNewCommittee({ name: '', description: '' });
      setCreateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create committee');
    }
  };

  const handleCreateFromTemplates = async () => {
    if (selectedTemplates.length === 0) {
      toast.error('Please select at least one committee');
      return;
    }

    setCreatingFromTemplate(true);
    try {
      const templatesToCreate = COMMITTEE_TEMPLATES.filter(t => selectedTemplates.includes(t.id));
      
      for (const template of templatesToCreate) {
        await axios.post(`${API_URL}/api/event/committees?event_id=${selectedEventId}`, {
          name: template.name,
          description: template.description
        });
      }
      
      toast.success(`Created ${templatesToCreate.length} committees successfully`);
      setTemplateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create committees');
    } finally {
      setCreatingFromTemplate(false);
    }
  };

  const handleAssignMember = async (e) => {
    e.preventDefault();
    if (!selectedCommittee) return;
    try {
      await axios.post(`${API_URL}/api/event/committees/${selectedCommittee.id}/members`, assignData);
      toast.success('Member assigned successfully');
      setAssignData({ user_id: '', role: 'member' });
      setAssignDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign member');
    }
  };

  const handleRemoveMember = async (committeeId, memberId, isHead = false) => {
    if (!window.confirm('Remove this member from the committee?')) return;
    try {
      // For head, we update with null head_id
      if (isHead) {
        await axios.post(`${API_URL}/api/event/committees/${committeeId}/members`, {
          user_id: memberId,
          role: 'remove_head'
        });
      } else {
        await axios.delete(`${API_URL}/api/event/committees/${committeeId}/members/${memberId}`);
      }
      toast.success('Member removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const handleDeleteCommittee = async (committeeId) => {
    if (!window.confirm('Delete this committee? All members will be removed.')) return;
    try {
      await axios.delete(`${API_URL}/api/event/committees/${committeeId}`);
      toast.success('Committee deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete committee');
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const getAvailableMembers = (committee) => {
    const assignedIds = [committee?.head?.id, ...(committee?.member_ids || [])].filter(Boolean);
    return attendees.filter(m => !assignedIds.includes(m.id));
  };

  const getTemplateIcon = (templateId) => {
    const template = COMMITTEE_TEMPLATES.find(t => t.id === templateId);
    return template?.icon || Building2;
  };

  const getTemplateColor = (committeeName) => {
    const template = COMMITTEE_TEMPLATES.find(t => 
      committeeName.toLowerCase().includes(t.id) || 
      t.name.toLowerCase() === committeeName.toLowerCase()
    );
    return template?.color || 'bg-gray-500';
  };

  const toggleTemplate = (templateId) => {
    setSelectedTemplates(prev => 
      prev.includes(templateId) 
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-maroon" />
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="text-center py-12">
        <Building2 className="h-16 w-16 mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-600">No events found</h3>
        <p className="text-gray-400 mt-1">Create an event first to manage committees</p>
      </div>
    );
  }

  const selectedEvent = events.find(e => e.id === selectedEventId);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="committees-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-maroon flex items-center gap-3">
            <Building2 className="h-8 w-8" />
            Committees
          </h1>
          <p className="text-gray-600 mt-1">
            {committees.length} committees • {selectedEvent?.event_name}
          </p>
        </div>
        
        {/* Event Selector */}
        {events.length > 1 && (
          <Select value={selectedEventId} onValueChange={setSelectedEventId}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Select Event" />
            </SelectTrigger>
            <SelectContent>
              {events.map(event => (
                <SelectItem key={event.id} value={event.id}>
                  {event.event_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Action Buttons */}
      {isOrganizer && isOrganizer() && (
        <div className="flex flex-wrap gap-3">
          <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-maroon hover:bg-maroon-hover text-white rounded-full" data-testid="setup-committees-btn">
                <Shield className="h-4 w-4 mr-2" />
                Setup Standard Committees
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-maroon text-xl">Setup Committees</DialogTitle>
                <DialogDescription>
                  Select the committees you need for your reunion. Required committees are pre-selected.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                {/* Required Committees */}
                <div>
                  <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    Core Committees (Recommended)
                  </h3>
                  <div className="grid gap-3">
                    {COMMITTEE_TEMPLATES.filter(t => t.isRequired).map((template) => {
                      const Icon = template.icon;
                      const isSelected = selectedTemplates.includes(template.id);
                      return (
                        <div 
                          key={template.id}
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-maroon bg-maroon/5' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => toggleTemplate(template.id)}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox 
                              checked={isSelected} 
                              onCheckedChange={() => toggleTemplate(template.id)}
                              className="mt-1"
                            />
                            <div className={`p-2 rounded-lg ${template.color}`}>
                              <Icon className="h-5 w-5 text-white" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{template.name}</h4>
                              <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {template.appModules.map(mod => (
                                  <Badge key={mod} variant="outline" className="text-xs">
                                    {mod}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Optional Committees */}
                <div>
                  <h3 className="font-semibold text-gray-700 mb-3">Optional Committees</h3>
                  <div className="grid gap-3">
                    {COMMITTEE_TEMPLATES.filter(t => !t.isRequired).map((template) => {
                      const Icon = template.icon;
                      const isSelected = selectedTemplates.includes(template.id);
                      return (
                        <div 
                          key={template.id}
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-maroon bg-maroon/5' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => toggleTemplate(template.id)}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox 
                              checked={isSelected} 
                              onCheckedChange={() => toggleTemplate(template.id)}
                              className="mt-1"
                            />
                            <div className={`p-2 rounded-lg ${template.color}`}>
                              <Icon className="h-5 w-5 text-white" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{template.name}</h4>
                              <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {template.appModules.map(mod => (
                                  <Badge key={mod} variant="outline" className="text-xs">
                                    {mod}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="flex gap-2 justify-end mt-6 pt-4 border-t">
                <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateFromTemplates}
                  className="bg-maroon hover:bg-maroon-hover text-white"
                  disabled={creatingFromTemplate || selectedTemplates.length === 0}
                  data-testid="create-from-templates-btn"
                >
                  {creatingFromTemplate ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>Create {selectedTemplates.length} Committees</>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-maroon text-maroon hover:bg-maroon/5 rounded-full" data-testid="create-custom-committee-btn">
                <Plus className="h-4 w-4 mr-2" />
                Create Custom Committee
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading text-maroon">Create Custom Committee</DialogTitle>
                <DialogDescription>Add a custom committee for your reunion</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateCommittee} className="space-y-4">
                <div className="space-y-2">
                  <Label>Committee Name</Label>
                  <Input
                    value={newCommittee.name}
                    onChange={(e) => setNewCommittee({ ...newCommittee, name: e.target.value })}
                    placeholder="e.g., Entertainment Committee"
                    required
                    data-testid="committee-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={newCommittee.description}
                    onChange={(e) => setNewCommittee({ ...newCommittee, description: e.target.value })}
                    placeholder="What does this committee handle?"
                    data-testid="committee-description-input"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-maroon hover:bg-maroon-hover text-white" data-testid="submit-committee-btn">
                    Create Committee
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Committees Grid */}
      {committees.length === 0 ? (
        <Card className="border-0 shadow-sm bg-white">
          <CardContent className="p-12 text-center">
            <Building2 className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-600">No committees yet</h3>
            <p className="text-gray-400 mt-1 mb-6">Set up your organizing committees to get started</p>
            <Button 
              onClick={() => setTemplateDialogOpen(true)}
              className="bg-maroon hover:bg-maroon-hover text-white rounded-full"
            >
              <Shield className="h-4 w-4 mr-2" />
              Setup Standard Committees
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {committees.map((committee) => {
            const templateColor = getTemplateColor(committee.name);
            return (
              <Card 
                key={committee.id} 
                className="border-0 shadow-sm bg-white hover:shadow-lg transition-shadow duration-300"
                data-testid={`committee-card-${committee.id}`}
              >
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${templateColor}`}>
                        <Building2 className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <CardTitle className="font-heading text-xl text-maroon">{committee.name}</CardTitle>
                        <CardDescription className="mt-1">{committee.description}</CardDescription>
                      </div>
                    </div>
                    {isOrganizer && isOrganizer() && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteCommittee(committee.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid={`delete-committee-${committee.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Committee Head */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Crown className="h-4 w-4 text-gold" />
                      <span className="text-sm font-medium text-gray-600">Committee Head</span>
                    </div>
                    {committee.head ? (
                      <div className="flex items-center justify-between p-3 bg-gold/10 rounded-xl">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10 border-2 border-gold">
                            <AvatarFallback className="bg-gold text-cream-dark">
                              {getInitials(committee.head.first_name, committee.head.last_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{committee.head.first_name} {committee.head.last_name}</p>
                            <p className="text-xs text-gray-500">{committee.head.email}</p>
                          </div>
                        </div>
                        {isOrganizer && isOrganizer() && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveMember(committee.id, committee.head.id, true)}
                            className="text-red-500 hover:text-red-600"
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic p-3 bg-gray-50 rounded-xl">No head assigned</p>
                    )}
                  </div>

                  {/* Committee Members */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="h-4 w-4 text-blue-500" />
                      <span className="text-sm font-medium text-gray-600">Members ({committee.members?.length || 0})</span>
                    </div>
                    {committee.members?.length > 0 ? (
                      <div className="space-y-2">
                        {committee.members.map((member) => (
                          <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback className="bg-blue-500 text-white text-sm">
                                  {getInitials(member.first_name, member.last_name)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium text-sm">{member.first_name} {member.last_name}</p>
                              </div>
                            </div>
                            {isOrganizer && isOrganizer() && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveMember(committee.id, member.id)}
                                className="text-red-500 hover:text-red-600 text-xs"
                              >
                                Remove
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic p-3 bg-gray-50 rounded-xl">No members yet</p>
                    )}
                  </div>

                  {/* Add Member Button */}
                  {isOrganizer && isOrganizer() && (
                    <Dialog open={assignDialogOpen && selectedCommittee?.id === committee.id} onOpenChange={(open) => {
                      setAssignDialogOpen(open);
                      if (open) setSelectedCommittee(committee);
                    }}>
                      <DialogTrigger asChild>
                        <Button 
                          variant="outline" 
                          className="w-full border-dashed border-maroon text-maroon hover:bg-maroon/5"
                          onClick={() => setSelectedCommittee(committee)}
                          data-testid={`add-member-btn-${committee.id}`}
                        >
                          <UserPlus className="h-4 w-4 mr-2" />
                          Add Member
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="font-heading text-maroon">Add Member to {committee.name}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleAssignMember} className="space-y-4">
                          <div className="space-y-2">
                            <Label>Select Attendee</Label>
                            <Select value={assignData.user_id} onValueChange={(v) => setAssignData({ ...assignData, user_id: v })}>
                              <SelectTrigger data-testid="select-member">
                                <SelectValue placeholder="Choose an attendee" />
                              </SelectTrigger>
                              <SelectContent>
                                {getAvailableMembers(committee).map((m) => (
                                  <SelectItem key={m.id} value={m.id}>
                                    {m.first_name} {m.last_name}
                                    {m.batch_year && ` (Batch ${m.batch_year})`}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Role</Label>
                            <Select value={assignData.role} onValueChange={(v) => setAssignData({ ...assignData, role: v })}>
                              <SelectTrigger data-testid="select-role">
                                <SelectValue placeholder="Select role" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="head">Committee Head</SelectItem>
                                <SelectItem value="member">Committee Member</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex gap-2 justify-end">
                            <Button type="button" variant="outline" onClick={() => setAssignDialogOpen(false)}>
                              Cancel
                            </Button>
                            <Button type="submit" className="bg-maroon hover:bg-maroon-hover text-white" data-testid="assign-member-btn">
                              Assign Member
                            </Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Committee Responsibilities Reference */}
      {committees.length > 0 && (
        <Card className="border-0 shadow-sm bg-gradient-to-br from-cream to-cream-paper">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-maroon">Committee Reference Guide</CardTitle>
            <CardDescription>Standard committee responsibilities and app modules</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-semibold">Committee</th>
                    <th className="text-left py-2 px-3 font-semibold">App Modules Used</th>
                  </tr>
                </thead>
                <tbody>
                  {COMMITTEE_TEMPLATES.slice(0, 6).map(template => (
                    <tr key={template.id} className="border-b border-gray-100">
                      <td className="py-2 px-3 font-medium">{template.name.replace(' Committee', '')}</td>
                      <td className="py-2 px-3">
                        <div className="flex flex-wrap gap-1">
                          {template.appModules.map(mod => (
                            <Badge key={mod} variant="outline" className="text-xs">
                              {mod}
                            </Badge>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Committees;
